/**
 * Pure helpers shared between the me-audit full page (`/me/audit`) and
 * the me-audit ticker section in the member persistent right panel
 * (IA-rework PR 2, #196).
 *
 * Extracted from `web/src/routes/MeAudit.tsx` so the panel-side ticker
 * can render the same plain-English action labels (and the same
 * actor/target role distinction) without duplicating the table.
 *
 * The shared piece is the action-to-summary mapping + role-aware
 * voicing ("You ..." vs "Your team lead ..."). The full page renders
 * these inside a 640px column with role chips; the ticker renders a
 * compact row inside a 330px panel. Only the formatters / summariser
 * + the `isTargetRow` predicate are shared — the JSX shape stays
 * separate per `feedback_ui_section_focal_component.md`.
 */
import type { components } from "../api/schema.gen";

import { formatAbsolute, formatRelativeTime, humaniseAction } from "./teamAuditFormat";

type AuditEventResponse = components["schemas"]["AuditEventResponse"];

export interface SummaryParts {
  /** One-line plain-English description. */
  label: string;
  /** Optional secondary detail derived from `payload`. */
  detail?: string;
}

/** Re-export for callers (the ticker) that only need the me-audit
 * surface — saves a second import. */
export { formatAbsolute, formatRelativeTime };

export function isMeAuditTargetRow(event: AuditEventResponse): boolean {
  return event.role === "target";
}

/**
 * Plain-English row summaries for the member's own feed. Target rows
 * (LL-D4) — written by a lead about the caller — voice as "Your team
 * lead ...". Actor rows voice as "You ...". Anything unmapped falls
 * through to a humanised version of the action string.
 *
 * Keep these calm + factual. Trustable per brand pillar means the
 * worker reads the row and feels they could verify it, not that it's
 * being reported on them.
 */
export function summariseMeAuditEvent(event: AuditEventResponse): SummaryParts {
  const payload = (event.payload ?? {}) as Record<string, unknown>;

  if (event.role === "target") {
    switch (event.action) {
      case "submission.lead_bypass_write":
        return withOptionalDetail(
          "Your team lead edited your submission",
          detailFromLeaveCount(payload),
        );
      case "submission.bulk_blocks_write":
        return { label: "Your team lead updated your availability blocks" };
      case "member.patched":
        return withOptionalDetail(
          "Your team lead updated your team profile",
          detailFromMemberPatch(payload),
        );
      case "member.attributes.updated":
        return { label: "Your team lead updated your team attributes" };
      default:
        return { label: `Your team lead: ${humaniseAction(event.action)}` };
    }
  }

  switch (event.action) {
    case "submission.created":
      return withOptionalDetail("You submitted your availability", detailFromLeaveCount(payload));
    case "submission.bulk_blocks_write":
      return { label: "You updated your availability blocks" };
    case "submission.lead_bypass_write":
      return withOptionalDetail(
        "Your lead edited this submission on your behalf",
        detailFromLeaveCount(payload),
      );
    case "member.patched":
      return withOptionalDetail("Your team profile was updated", detailFromMemberPatch(payload));
    case "leave.dispute_filed":
      return { label: "You raised a dispute on a leave decision" };
    case "leave.decision_recorded":
      return { label: "A leave decision was recorded" };
    case "invite.accepted":
      return { label: "You joined a team" };
    case "bid.placed":
      return { label: "You placed a bid" };
    case "member_message.sent":
      return { label: "You sent a message to your lead" };
    case "schedule_republished":
      return { label: "Your team's roster was re-published" };
    case "account.deleted":
      return { label: "You requested account deletion" };
    default:
      return { label: humaniseAction(event.action) };
  }
}

/** Helper so the `detail` key only appears when defined — keeps the
 *  return shape compatible with `exactOptionalPropertyTypes: true`. */
function withOptionalDetail(label: string, detail: string | undefined): SummaryParts {
  return detail !== undefined ? { label, detail } : { label };
}

function detailFromLeaveCount(payload: Record<string, unknown>): string | undefined {
  const leaveDates = payload.leave_dates;
  if (Array.isArray(leaveDates)) {
    const n = leaveDates.length;
    if (n === 0) return "No days off requested.";
    return `${n} day${n === 1 ? "" : "s"} off.`;
  }
  return undefined;
}

function detailFromMemberPatch(payload: Record<string, unknown>): string | undefined {
  const candidate =
    (payload.changes as Record<string, unknown> | undefined) ??
    (payload.after as Record<string, unknown> | undefined);
  if (candidate && typeof candidate === "object") {
    const keys = Object.keys(candidate);
    if (keys.length > 0) {
      return `Fields changed: ${keys.join(", ")}.`;
    }
  }
  return undefined;
}
